import { RJSFSchema, UiSchema } from '@rjsf/utils'
import { mainWidgets } from './main-widgets'
import definitions from '../../../../../schemas/definitions.json'
import conditionals from '../../../../../schemas/conditionals.json'
import { payloadWidgets } from './payload-widgets'
import {
  SkrottlappScanFieldStorfe,
  SkrottlappScanFieldSvin,
  SkrottlappScanFieldSau
} from '../components/form/fields/SkrottlappScanField'
import { ReactNode } from 'react'
import {
  BracketsCurlyIcon,
  CopyIcon,
  CubeIcon,
  HashIcon,
  ListBulletsIcon,
  QuestionIcon,
  TextTIcon,
  ToggleLeftIcon
} from 'prove-common'
import { SampleFormContext, SampleFormData } from '../types/types'

const schemaDefinitions = definitions as RJSFSchema['definitions']
const schemaConditionals = conditionals as RJSFSchema
const skrottlappCountryNames: Record<string, string> = {
  '001': 'USA',
  '002': 'Canada',
  '007': 'Russland',
  '030': 'Hellas',
  '031': 'Nederland',
  '032': 'Belgia',
  '033': 'Frankrike',
  '034': 'Spania',
  '036': 'Ungarn',
  '039': 'Italia',
  '040': 'Romania',
  '041': 'Sveits',
  '043': 'Østerrike',
  '044': 'Storbritannia',
  '045': 'Danmark',
  '046': 'Sverige',
  '047': 'Norge',
  '048': 'Polen',
  '049': 'Tyskland',
  '052': 'Mexico',
  '054': 'Argentina',
  '055': 'Brasil',
  '061': 'Australia',
  '064': 'New Zealand',
  '081': 'Japan',
  '090': 'Tyrkia',
  '298': 'Færøyene',
  '299': 'Grønland',
  '351': 'Portugal',
  '352': 'Luxembourg',
  '353': 'Irland',
  '354': 'Island',
  '355': 'Albania'
}
export type WidgetState = 'hidden' | 'locked' | 'unselected' | 'selected'

export interface SchemaWidget {
  id: string
  title?: string
  icon: ReactNode
  propKey: string
  schema?: RJSFSchema
  uiSchema?: UiSchema
  conditionals?: RJSFSchema[]
  isRequired: boolean
  state: WidgetState
  isFallback?: boolean
}

export type SchemaWidgets = SchemaWidget[]

export interface SchemaWidgetGroup {
  id: string
  title: string
  icon: ReactNode
  widgets: SchemaWidgets
}

export type SchemaWidgetGroups = SchemaWidgetGroup[]

function getLikelySkrottlappYear(
  yearDigit: number,
  referenceYear: number = new Date().getFullYear()
): number {
  let year = Math.floor(referenceYear / 10) * 10 + yearDigit
  if (year > referenceYear + 1) {
    year -= 10
  }
  return year
}

function getIsoWeekDate(
  year: number,
  week: number,
  isoDay: number
): Date | null {
  if (week < 1 || isoDay < 1 || isoDay > 7) return null

  const maxIsoWeek = getIsoWeekNumber(new Date(Date.UTC(year, 11, 28)))
  if (week > maxIsoWeek) return null

  const jan4 = new Date(Date.UTC(year, 0, 4))
  const jan4IsoDay = jan4.getUTCDay() || 7
  const weekOneMonday = new Date(jan4)
  weekOneMonday.setUTCDate(jan4.getUTCDate() - jan4IsoDay + 1)

  const date = new Date(weekOneMonday)
  date.setUTCDate(weekOneMonday.getUTCDate() + (week - 1) * 7 + (isoDay - 1))
  return date
}

function getIsoWeekNumber(date: Date): number {
  const normalizedDate = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  )
  const isoDay = normalizedDate.getUTCDay() || 7
  normalizedDate.setUTCDate(normalizedDate.getUTCDate() + 4 - isoDay)

  const yearStart = new Date(Date.UTC(normalizedDate.getUTCFullYear(), 0, 1))
  return Math.ceil(
    ((normalizedDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
  )
}

function formatSkrottlappSlaktedato(value: string): string | null {
  if (!/^\d\d\d[1-7]$/.test(value)) return null

  const yearDigit = Number.parseInt(value[0], 10)
  const week = Number.parseInt(value.slice(1, 3), 10)
  const isoDay = Number.parseInt(value[3], 10)
  const year = getLikelySkrottlappYear(yearDigit)
  const date = getIsoWeekDate(year, week, isoDay)

  if (date == null) return null

  return date.toLocaleDateString('no-NO', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

function formatSkrottlappSubValue(subKey: string, rawValue: string): string {
  if (subKey === 'slaktedato') {
    const formattedDate = formatSkrottlappSlaktedato(rawValue)
    return formattedDate ? `${formattedDate} (${rawValue})` : rawValue
  }

  if (subKey === 'foedeland') {
    const countryName = skrottlappCountryNames[rawValue]
    return countryName ? `${countryName} (${rawValue})` : rawValue
  }

  if (subKey === 'alder' && /^\d+$/.test(rawValue)) {
    return rawValue.replace(/^0+/, '') || '0'
  }

  return rawValue
}

function formatReindeerScanValue(
  subKey: string,
  rawValue: string,
  sexCode?: string
): string {
  if (subKey === 'kjoenn') {
    if (rawValue === '0') return 'Hann (0)'
    if (rawValue === '1') return 'Hunn (1)'
    return rawValue
  }
  if (subKey !== 'alder') {
    return formatSkrottlappSubValue(subKey, rawValue)
  }

  const ageGroups: Record<string, string> = {
    '0': '0–1 år',
    '1': '1–2 år',
    '2': '2–3 år'
  }
  if (rawValue === '3' && sexCode === '0') return '3–4 år (3)'
  if (rawValue === '3' && sexCode === '1') return '3 år og eldre (3)'
  if (rawValue === '4' && sexCode === '0') return 'Over 4 år (4)'
  return ageGroups[rawValue] ? `${ageGroups[rawValue]} (${rawValue})` : rawValue
}

export const parsePayloadSchema = (
  payloadSchema: string | undefined
): RJSFSchema | undefined =>
  payloadSchema == null ? undefined : JSON.parse(payloadSchema)

export const getSampleRegistrationSchema = (
  payloadSchema: RJSFSchema | undefined,
  isEditing: boolean,
  useLokalitetWidget = false,
  isPartiallyEditable: boolean = false
): RJSFSchema => {
  const schemaWithNormalizedConditionals =
    normalizePayloadConditionals(payloadSchema)
  const widgets = mainWidgets(
    schemaWithNormalizedConditionals,
    isEditing,
    useLokalitetWidget,
    isPartiallyEditable
  )
  const properties: Record<string, RJSFSchema> = {}
  const required: string[] = []
  for (const widget of widgets) {
    if (widget.schema) properties[widget.propKey] = widget.schema
    if (widget.isRequired) required.push(widget.propKey)
  }
  return {
    definitions: schemaDefinitions,
    conditionals: schemaConditionals,
    type: 'object',
    properties,
    required
  }
}

const AKTIVITET_UI_SCHEMAS: Record<string, UiSchema> = {
  AKTIVITET$STORFEHOLD: { 'ui:field': SkrottlappScanFieldStorfe },
  AKTIVITET$SVINEHOLD: { 'ui:field': SkrottlappScanFieldSvin },
  AKTIVITET$SAUEHOLD: { 'ui:field': SkrottlappScanFieldSau }
}

// Derives which fields are revealed by each conditional block by reading
// then.properties keys from conditionals.json. This way, new conditionals
// are automatically picked up without needing to update a hardcoded map.
const conditionalFollowers: Record<string, string[]> = Object.fromEntries(
  Object.entries(conditionals).flatMap(([parentKey, block]) => {
    const thenProps = (
      block as { then?: { properties?: Record<string, unknown> } }
    )?.then?.properties
    if (!thenProps || typeof thenProps !== 'object') return []
    return [[parentKey, Object.keys(thenProps)]]
  })
)

// Tracks which conditional blocks have x-followers-order: "before", meaning
// their follower fields should be inserted BEFORE the parent field in ui:order.
const conditionalFollowersBefore: Set<string> = new Set(
  Object.entries(conditionals)
    .filter(
      ([, block]) =>
        (block as { 'x-followers-order'?: string })['x-followers-order'] ===
        'before'
    )
    .map(([parentKey]) => parentKey)
)

const normalizePayloadConditionals = (
  payloadSchema: RJSFSchema | undefined
): RJSFSchema | undefined => {
  if (!payloadSchema?.allOf) return payloadSchema

  const schema = { ...payloadSchema }
  delete schema.allOf
  const dependencies = { ...(payloadSchema.dependencies ?? {}) }
  const allOf: RJSFSchema['allOf'] = []
  const required = new Set(payloadSchema.required ?? [])

  for (const conditional of payloadSchema.allOf) {
    if (typeof conditional !== 'object' || conditional === null) {
      allOf.push(conditional)
      continue
    }

    const resolvedConditional = resolveConditionalSchema(conditional)
    const trigger = getConditionalTrigger(resolvedConditional)
    if (
      !trigger ||
      (resolvedConditional?.else && !required.has(trigger)) ||
      dependencies[trigger]
    ) {
      allOf.push(conditional)
      continue
    }

    dependencies[trigger] = conditional
  }

  return {
    ...schema,
    ...(allOf.length > 0 ? { allOf } : {}),
    dependencies
  }
}

const resolveConditionalSchema = (
  schema: RJSFSchema
): RJSFSchema | undefined => {
  if (typeof schema.$ref !== 'string') return schema

  const { source, key } = extractRefKey(schema.$ref)
  if (source !== 'conditionals') return undefined

  return conditionals[key as keyof typeof conditionals] as RJSFSchema
}

const getConditionalTrigger = (schema: RJSFSchema | undefined) => {
  const ifSchema = schema?.if
  if (typeof ifSchema !== 'object' || ifSchema === null) return undefined

  const required = ifSchema.required
  if (!Array.isArray(required) || required.length !== 1) return undefined

  return String(required[0])
}

export const getPayloadUiSchema = (payloadSchema?: RJSFSchema): UiSchema => {
  const uiSchemas: UiSchema = {}

  // Legacy schemas stored 'aktivitet' at the top level of the payload schema
  // (e.g. 'AKTIVITET$STORFEHOLD'). Read it once here so it can be used as a
  // fallback when a property's $ref is no longer present in payloadWidgets.
  const aktivitet =
    typeof (payloadSchema as Record<string, unknown> | undefined)?.aktivitet ===
    'string'
      ? ((payloadSchema as Record<string, unknown>).aktivitet as string)
      : undefined

  // Build a map from each property key to the conditional keys in allOf that
  // govern it. The conditional block's if.properties tells us which property
  // it depends on, so we invert that relationship here.
  const propToConditionalKeys = new Map<string, string[]>()
  ;(
    (payloadSchema as { allOf?: { $ref?: string }[] } | undefined)?.allOf ?? []
  ).forEach((ref) => {
    const refStr = ref.$ref
    if (!refStr) return
    const { source, key: condKey } = extractRefKey(refStr)
    if (source !== 'conditionals') return
    const block = conditionals[condKey as keyof typeof conditionals] as {
      if?: { properties?: Record<string, unknown> }
    }
    const ifProps = block?.if?.properties
    if (!ifProps) return
    for (const propKey of Object.keys(ifProps)) {
      const existing = propToConditionalKeys.get(propKey) ?? []
      existing.push(condKey)
      propToConditionalKeys.set(propKey, existing)
    }
  })

  // Step 1: Add uiSchemas for properties defined in payloadSchema, preserving
  // the user-defined order as the basis for ui:order.
  const schemaKeys: string[] = []
  Object.entries(payloadSchema?.properties ?? {}).forEach(([key, schema]) => {
    const condKeys = propToConditionalKeys.get(key) ?? []

    // Collect follower fields from all matching conditionals, split by order.
    const followersBefore: string[] = []
    const followersAfter: string[] = []
    for (const condKey of condKeys) {
      const followers = conditionalFollowers[condKey] ?? []
      if (conditionalFollowersBefore.has(condKey)) {
        followersBefore.push(...followers)
      } else {
        followersAfter.push(...followers)
      }
    }

    // If this field has conditional followers marked as "before", insert them
    // BEFORE the parent field so they appear above it in the rendered form.
    if (followersBefore.length > 0) {
      schemaKeys.push(...followersBefore)
    }
    schemaKeys.push(key)
    // If this field has conditional followers (not "before"), insert them
    // immediately after so they are always rendered together regardless of
    // alphabetical sorting.
    if (followersAfter.length > 0) {
      schemaKeys.push(...followersAfter)
    }
    if (typeof schema !== 'object' || schema === null || !('$ref' in schema)) {
      return
    }
    const ref = (schema as { $ref: string }).$ref
    const widget = payloadWidgets.find((w) => w.schema?.$ref === ref)
    if (widget) {
      uiSchemas[key] = widget.uiSchema ?? {}
      return
    }
    // No matching widget found — fall back to the top-level aktivitet value so
    // legacy schemas with '#/definitions/skrottlappScan' still get the right
    // field component.
    if (aktivitet && AKTIVITET_UI_SCHEMAS[aktivitet]) {
      uiSchemas[key] = AKTIVITET_UI_SCHEMAS[aktivitet]
    }
  })

  // Step 2: Collect uiSchemas from payloadWidgets not already included
  payloadWidgets.forEach((widget) => {
    if (!uiSchemas[widget.propKey]) {
      uiSchemas[widget.propKey] = widget.uiSchema ?? {}
    }
  })

  return {
    ...uiSchemas,
    'ui:order': [...schemaKeys, '*']
  }
}

export const getSampleRegistrationUiSchema = (
  payloadSchema: RJSFSchema | undefined,
  isEditing: boolean,
  useLokalitetWidget = false,
  isPartiallyEditable: boolean = false
): UiSchema<SampleFormData, RJSFSchema, SampleFormContext> => {
  const widgets = mainWidgets(
    payloadSchema,
    isEditing,
    useLokalitetWidget,
    isPartiallyEditable
  )
  const uiSchemas: UiSchema = {}
  const keys: string[] = []
  for (const widget of widgets) {
    if (widget.uiSchema) {
      uiSchemas[widget.propKey] = widget.uiSchema
    }
    keys.push(widget.propKey)
  }
  return {
    ...uiSchemas,
    'ui:order': [...keys, '*']
  } as UiSchema<SampleFormData, RJSFSchema, SampleFormContext>
}

export const getPropertiesFromSchema = (
  value?: string
): RJSFSchema['properties'] | undefined => {
  if (!value) return undefined
  try {
    const parsedValue: RJSFSchema = JSON.parse(value)
    return parsedValue?.properties
  } catch {
    return undefined
  }
}

export const turnPropertiesToWidgets = (
  properties: RJSFSchema,
  requiredProperties: string[]
) => {
  const widgets: SchemaWidgets = []
  for (const [key, schema] of Object.entries(properties)) {
    const widget = payloadWidgets.find(
      (w) => w.schema?.$ref === schema.$ref && w.propKey === key
    )
    const isRequired = requiredProperties.includes(key)
    if (widget) {
      widgets.push({
        ...widget,
        schema: schema as RJSFSchema,
        isRequired,
        state: 'selected'
      })
    } else {
      const title = searchSchemaForTitle(
        {
          properties
        },
        key
      )
      widgets.push({
        id: key,
        title: title ?? key,
        icon: <CubeIcon />,
        propKey: key,
        schema: schema as RJSFSchema,
        isRequired,
        isFallback: true,
        state: 'locked'
      })
    }
  }
  return widgets
}

export const getSelectableWidgets = (selectedWidgets: SchemaWidgets) => {
  // Only add widgets from payloadWidgets that are not hidden and not already selected
  const selectedIds = new Set(selectedWidgets.map((w) => w.id))
  const additional = payloadWidgets.filter(
    (w) => !selectedIds.has(w.id) && w.state !== 'hidden'
  )
  return [...selectedWidgets, ...additional]
}

export const groupWidgets = (widgets: SchemaWidgets): SchemaWidgetGroups => {
  // Group widgets by propKey.
  const grouped: { [propKey: string]: SchemaWidget[] } = {}

  for (const widget of widgets) {
    const propKey = widget.propKey
    if (!grouped[propKey]) {
      grouped[propKey] = []
    }
    grouped[propKey].push(widget)
  }

  return Object.entries(grouped).map(([propKey, widgets]) => ({
    id: propKey,
    ...getTitleAndIcon(widgets),
    widgets
  }))
}

export const sortWidgets = (widgets: SchemaWidgets) =>
  widgets.toSorted((a, b) => {
    // locked and selected both rank as 1 so that locked widgets keep their
    // schema-defined position relative to other selected/locked widgets;
    // only unselected and hidden are pushed to the back.
    const stateOrder = { locked: 1, selected: 1, unselected: 2, hidden: 3 }
    return stateOrder[a.state] - stateOrder[b.state]
  })

export const getTitleAndIcon = (
  widgets: SchemaWidgets
): { title: string; icon: React.ReactNode } => {
  const { propKey, icon } = widgets[0]
  switch (propKey) {
    case 'entityId':
      return {
        title: 'ID',
        icon: <HashIcon />
      }
    case 'subSamples':
      return {
        title: 'Delprøver',
        icon: <CopyIcon />
      }
    case 'skrottlappScan':
      return {
        title: 'Skrottlapp',
        icon
      }
    default:
      return {
        title: getWidgetTitle(widgets[0]),
        icon
      }
  }
}

function isObject(val: unknown): val is Record<string, unknown> {
  return typeof val === 'object' && val !== null && !Array.isArray(val)
}

function hasTitle(obj: unknown): obj is { title: string } {
  return isObject(obj) && typeof obj.title === 'string'
}

function getRef(obj: unknown): string | undefined {
  return isObject(obj) ? (obj.$ref as string | undefined) : undefined
}

type RefSource = 'definitions' | 'conditionals' | null

function extractRefKey(ref: string): { source: RefSource; key: string } {
  const match = new RegExp(/^#\/(definitions|conditionals)\/(.+)$/).exec(ref)
  if (match) {
    return { source: match[1] as RefSource, key: match[2] }
  }
  return { source: null, key: ref }
}

function resolveRefFromSources(ref: string): unknown {
  const { source, key } = extractRefKey(ref)
  if (source === 'definitions' && key in definitions) {
    return definitions[key as keyof typeof definitions]
  }
  if (source === 'conditionals' && key in conditionals) {
    return conditionals[key as keyof typeof conditionals]
  }
  return null
}

function resolveSchemaRef(
  schema: unknown,
  visited = new Set<string>()
): unknown {
  if (!isObject(schema) || typeof schema.$ref !== 'string') return schema
  if (visited.has(schema.$ref)) return null

  visited.add(schema.$ref)
  return resolveSchemaRef(resolveRefFromSources(schema.$ref), visited)
}

/**
 * Returns the description of a widget's schema.
 * Resolves $ref references to look up the description from definitions.
 */
export const getWidgetDescription = (
  schema: RJSFSchema | undefined
): string | undefined => {
  if (!schema) return undefined
  if (typeof schema.description === 'string') return schema.description
  const resolved = resolveSchemaRef(schema)
  if (isObject(resolved) && typeof resolved.description === 'string') {
    return resolved.description
  }
  return undefined
}

/**
 * Returns the display title for a widget. Uses the widget's own title field
 * first (if set), then falls back to the title from definitions.json resolved
 * via the widget schema's $ref, and finally falls back to widget.id.
 */
export function getWidgetTitle(widget: SchemaWidget): string {
  if (widget.title != null) return widget.title
  const resolved = resolveSchemaRef(widget.schema)
  if (isObject(resolved) && typeof resolved.title === 'string') {
    return resolved.title
  }
  return widget.id
}

/**
 * Returns the sub-properties of an object widget's resolved $ref definition.
 * Returns null if the widget schema is not an object with sub-properties.
 */
export function getWidgetSubProperties(
  schema: RJSFSchema | undefined
): Array<{ key: string; title: string }> | null {
  if (!schema) return null
  const resolved = resolveSchemaRef(schema)
  if (!isObject(resolved) || resolved.type !== 'object') return null
  if (!isObject(resolved.properties)) return null
  const result: Array<{ key: string; title: string }> = []
  for (const [key, prop] of Object.entries(resolved.properties)) {
    const title =
      isObject(prop) && typeof prop.title === 'string' ? prop.title : key
    result.push({ key, title })
  }
  return result.length > 0 ? result : null
}

/**
 * Returns an icon representing the JSON Schema type of a widget's schema.
 * Resolves $ref references to look up the type from definitions.
 */
export const getWidgetTypeIcon = (
  schema: RJSFSchema | undefined
): ReactNode => {
  let type: string | undefined

  if (schema?.type) {
    type = schema.type as string
  } else {
    const resolved = resolveSchemaRef(schema)
    if (isObject(resolved) && typeof resolved.type === 'string') {
      type = resolved.type
    }
  }

  switch (type) {
    case 'string':
      return <TextTIcon />
    case 'number':
    case 'integer':
      return <HashIcon />
    case 'boolean':
      return <ToggleLeftIcon />
    case 'array':
      return <ListBulletsIcon />
    case 'object':
      return <BracketsCurlyIcon />
    default:
      return <QuestionIcon />
  }
}

function handleTitle(title: string): string {
  return title === 'Alder' ? 'Alder i dager' : title
}

function getSubPropertiesForObjectEntry(
  prop: unknown
): Record<string, unknown> | null {
  const ref = getRef(prop)
  if (ref) {
    const resolved = resolveSchemaRef(prop)
    return isObject(resolved) && isObject(resolved.properties)
      ? resolved.properties
      : null
  }

  if (isObject(prop) && isObject(prop.properties)) {
    return prop.properties
  }

  return null
}

function getDirectPropertyTitle(
  schema: RJSFSchema,
  key: string,
  visited: Set<string>
): string | null {
  const properties = isObject(schema.properties) ? schema.properties : undefined
  const prop = properties?.[key]
  if (!prop) return null

  if (hasTitle(prop)) return handleTitle(prop.title)

  const ref = getRef(prop)
  if (!ref) return key

  const resolved = resolveSchemaRef(prop, visited)
  if (resolved && hasTitle(resolved)) return handleTitle(resolved.title)
  return key
}

function getThenSchema(subSchema: unknown): RJSFSchema | null {
  const resolved = resolveSchemaRef(subSchema)
  const thenSchema =
    isObject(resolved) && 'then' in resolved
      ? (resolved as { then?: unknown }).then
      : undefined

  return isObject(thenSchema) ? (thenSchema as RJSFSchema) : null
}

function searchAllOfForTitle(
  schema: RJSFSchema,
  key: string,
  visited: Set<string>
): string | null {
  if (!Array.isArray(schema.allOf)) return null

  for (const subSchema of schema.allOf) {
    const thenSchema = getThenSchema(subSchema)
    if (!thenSchema) continue

    const result = searchSchemaForTitle(thenSchema, key, visited)
    if (result) return result
  }

  return null
}

/**
 * Expands an object value into labelled sub-entries using sub-property titles
 * from the resolved $ref definition.
 */
export function getObjectSubEntries(
  schema: RJSFSchema,
  key: string,
  value: Record<string, unknown>
): Array<{ label: string; value: string; _key: string }> {
  const properties = isObject(schema.properties) ? schema.properties : {}
  const prop = properties[key]
  if (!prop) return []

  const subProperties = getSubPropertiesForObjectEntry(prop)
  if (!subProperties) return []

  const isReindeerScan =
    key === 'skrottlappScan' &&
    getRef(prop) === '#/definitions/skrottlappScanRein'
  const rawSex = typeof value.kjoenn === 'string' ? value.kjoenn : undefined

  const entries = Object.entries(value).flatMap(([subKey, subValue]) => {
    if (key === 'reinbeitedistrikt' && subKey === 'nr') return []
    if (subValue == null || subValue === '') return []

    const subProp = subProperties[subKey]
    const title =
      isObject(subProp) && typeof subProp.title === 'string'
        ? subProp.title
        : null
    if (!title) return []

    const rawValue = String(subValue)
    return [
      {
        label: title,
        value: isReindeerScan
          ? formatReindeerScanValue(subKey, rawValue, rawSex)
          : key === 'skrottlappScan'
            ? formatSkrottlappSubValue(subKey, rawValue)
            : rawValue,
        _key: `${key}.${subKey}`
      }
    ]
  })

  return entries
}

function collectPropertySchemaCandidates(
  schema: RJSFSchema,
  propKey: string
): unknown[] {
  const candidates: unknown[] = []
  const properties = isObject(schema.properties) ? schema.properties : {}

  if (propKey in properties) {
    candidates.push(properties[propKey])
  }

  if (!Array.isArray(schema.allOf)) return candidates

  for (const subSchema of schema.allOf) {
    const resolved = resolveSchemaRef(subSchema)
    if (!isObject(resolved)) continue

    for (const branch of ['then', 'else'] as const) {
      const branchSchema = resolved[branch]
      if (isObject(branchSchema)) {
        candidates.push(
          ...collectPropertySchemaCandidates(branchSchema, propKey)
        )
      }
    }
  }

  return candidates
}

function collectNestedPropertySchemaCandidates(
  schema: RJSFSchema,
  objectKey: string,
  propKey: string
): unknown[] {
  const candidates: unknown[] = []

  for (const candidate of collectPropertySchemaCandidates(schema, objectKey)) {
    const resolved = resolveSchemaRef(candidate)
    candidates.push(...collectObjectPropertySchemaCandidates(resolved, propKey))
  }

  return candidates
}

function collectObjectPropertySchemaCandidates(
  schema: unknown,
  propKey: string
): unknown[] {
  const candidates: unknown[] = []
  const resolved = resolveSchemaRef(schema)
  if (!isObject(resolved)) return candidates

  const properties = isObject(resolved.properties) ? resolved.properties : {}
  if (propKey in properties) {
    candidates.push(properties[propKey])
  }

  if (!Array.isArray(resolved.allOf)) return candidates

  for (const subSchema of resolved.allOf) {
    const subSchemaResolved = resolveSchemaRef(subSchema)
    if (!isObject(subSchemaResolved)) continue

    for (const branch of ['then', 'else'] as const) {
      const branchSchema = subSchemaResolved[branch]
      candidates.push(
        ...collectObjectPropertySchemaCandidates(branchSchema, propKey)
      )
    }
  }

  return candidates
}

function getEnumTitle(schema: unknown, value: string): string | null {
  const resolved = resolveSchemaRef(schema)
  if (!isObject(resolved) || !Array.isArray(resolved.oneOf)) return null

  for (const entry of resolved.oneOf) {
    if (
      isObject(entry) &&
      entry.const === value &&
      typeof entry.title === 'string'
    ) {
      return entry.title
    }
  }

  return null
}

/**
 * Looks up the display title for an enum value in definitions.json.
 * Searches oneOf entries for a matching const and returns its title.
 * Returns null if no match is found.
 *
 * Collects ALL candidate property schemas for propKey (from direct properties
 * and from allOf → then/else branches) and searches each one, so that a more
 * specific conditional definition (e.g. verdikjedeSvin) takes priority over a
 * generic one (e.g. verdikjede) when the value only exists in the former.
 */
export function resolveEnumTitleFromSchema(
  schema: RJSFSchema,
  propKey: string,
  value: string
): string | null {
  for (const candidate of collectPropertySchemaCandidates(schema, propKey)) {
    const title = getEnumTitle(candidate, value)
    if (title) return title
  }

  return null
}

export function resolveNestedEnumTitleFromSchema(
  schema: RJSFSchema,
  objectKey: string,
  propKey: string,
  value: string
): string | null {
  for (const candidate of collectNestedPropertySchemaCandidates(
    schema,
    objectKey,
    propKey
  )) {
    const title = getEnumTitle(candidate, value)
    if (title) return title
  }

  return null
}

/**
 * Recursively searches a JSON schema for a property title by key.
 */
export function searchSchemaForTitle(
  schema: RJSFSchema,
  key: string,
  visited: Set<string> = new Set()
): string | null {
  return (
    getDirectPropertyTitle(schema, key, visited) ??
    searchAllOfForTitle(schema, key, visited)
  )
}
